import React, { ReactNode, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  ButtonProps,
  Collapse,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';
import moment from 'moment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';

import { useNavigate } from 'react-router-dom';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../../components/V2/Table/type1/TableStyles';
import {
  getSubmissionsV2State,
  IStudentPerformanceData,
} from '../../../../../store/V2/SubmissionsSlice';
import { TP_COLORS } from '../../../../../constants';
import { msToMinutesSeconds } from '../../../../../helper';
import { useAppSelector } from '../../../../../hooks';
import TopicPerformanceTable from './TopicPerformanceTable';

export const getScoreColor = (value: number, average: number): string => {
  if (average === value) {
    return TP_COLORS.yellow.light;
  }
  if (average < value) {
    return TP_COLORS.green.light;
  }
  return TP_COLORS.orange.light;
};

export const getScoreStatus = (value: number, average: number): string => {
  if (average === value) {
    return 'Average';
  }
  if (average < value) {
    return 'Above Average';
  }
  return 'Below Average';
};

export const getTimeIcon = (value: number): ReactNode => {
  if (value > 0) {
    return <NorthIcon sx={{ width: 18, height: 15 }} />;
  }
  return <SouthIcon sx={{ width: 18, height: 15 }} />;
};

const typoProps: TypographyProps = {
  fontSize: '14px',
  color: TP_COLORS.gray.darken,
};

const buttonProps: ButtonProps = {
  color: 'secondary',
  variant: 'outlined',
  sx: {
    minWidth: '134px',
    fontWeight: 600,
    borderRadius: '8px',
  },
};

export const viewButtonProps: ButtonProps = {
  disableElevation: true,
  variant: 'contained',
  color: 'secondary',
  sx: {
    width: '96px',
    color: TP_COLORS.typo.white,
    backgroundColor: TP_COLORS.gray.darken,
    fontWeight: 600,
    borderRadius: '8px',
  },
};

interface IStudentPerformanceRowProps {
  rowData: IStudentPerformanceData;
}

const StudentPerformanceRow: React.FC<IStudentPerformanceRowProps> = ({
  rowData,
}) => {
  const navigate = useNavigate();

  // State
  const [open, setOpen] = useState(false);

  // Store
  const { courseSubmissionsData } = useAppSelector(getSubmissionsV2State);

  const courseAverageScore = courseSubmissionsData?.totalAverageScore || 0;
  const courseAverageTime = courseSubmissionsData?.totalAverageTime || 0;

  const createdAt = moment(rowData.lastSubmissionAt);

  const timeDif = courseAverageTime - rowData.averageTime;

  const handleViewClick = () => {
    const path = `${process.env.PUBLIC_URL}/portal/quiz-performance/${courseSubmissionsData?.courseId}/${rowData._id}`;

    navigate(path);
  };

  return (
    <React.Fragment key={rowData._id}>
      <StyledTableRow hover>
        <StyledTableCell>
          <Box display="flex" gap="10px" alignItems="center">
            <Avatar alt="Remy Sharp" src={rowData.avatar} />
            <Typography {...typoProps}>
              {rowData.firstname || ''} {rowData.lastname || ''}
            </Typography>
          </Box>
        </StyledTableCell>
        <StyledTableCell>
          <Stack>
            <Typography {...typoProps}>
              {createdAt.format('ddd MMM DD YYYY')}
            </Typography>
            <Typography fontSize="12px" color="#94A3B8">
              {createdAt.format('hh:mm A')}
            </Typography>
          </Stack>
        </StyledTableCell>
        <StyledTableCell>
          <Stack>
            <Typography {...typoProps}>{rowData.averageScore}/20</Typography>
            <Typography
              fontSize="12px"
              color={getScoreColor(rowData.averageScore, courseAverageScore)}
            >
              {getScoreStatus(rowData.averageScore, courseAverageScore)}
            </Typography>
          </Stack>
        </StyledTableCell>
        <StyledTableCell>
          <Stack>
            <Typography {...typoProps}>
              {msToMinutesSeconds(rowData.averageTime)}
            </Typography>
            {timeDif !== 0 && (
              <Box
                display="flex"
                alignItems="center"
                color={getScoreColor(courseAverageTime, rowData.averageTime)}
              >
                {getTimeIcon(timeDif)}
                <Typography fontSize="12px" marginLeft="2px">
                  {msToMinutesSeconds(Math.abs(timeDif))}
                </Typography>
              </Box>
            )}
          </Stack>
        </StyledTableCell>

        <StyledTableCell>
          <Box display="flex" gap="10px">
            <Button
              {...buttonProps}
              onClick={() => setOpen(!open)}
              endIcon={
                open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />
              }
            >
              Topic Wise
            </Button>
            <Button {...viewButtonProps} onClick={handleViewClick}>
              View
            </Button>
          </Box>
        </StyledTableCell>
      </StyledTableRow>
      <StyledTableRow key={rowData._id}>
        <StyledTableCell
          style={{ paddingBottom: 0, paddingTop: 0 }}
          colSpan={6}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: '16px 4px' }}>
              <TopicPerformanceTable rowData={rowData} />
            </Box>
          </Collapse>
        </StyledTableCell>
      </StyledTableRow>
    </React.Fragment>
  );
};

export default StudentPerformanceRow;
